import Skeleton from "@/components/Skeleton/Skeleton";
import { VisualizationProps } from "@/components/visualizations/Visualization";
import {
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import NoDataPoints from "../NoDataPoints/NoDataPoints";
import useVisualization from "../useVisualization";
import styles from "./LineChart.module.scss";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

export type LineData = { label: string; data: { label: string; value: number | null }[] }[];

export interface LineChartProps extends VisualizationProps {
  data: LineData;
  stacked?: boolean;
  showBullets?: boolean;
}

export default function LineChart(props: LineChartProps) {
  const { data, loading, stacked, showBullets } = props;
  const { formatLabel, colorPalette, lightColorPalette } = useVisualization(props);

  const chartData: ChartData<"line", (number | null)[], string> = {
    labels: data.length > 0 ? data[0].data.map((row) => row.label) : [],
    datasets: data.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data.map((row) => row.value),
      backgroundColor: lightColorPalette[index % data.length],
      borderColor: colorPalette[index % data.length],
      fill: stacked === true,
    })),
  };

  const options: ChartOptions<"line"> = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => formatLabel(context.raw),
        },
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: showBullets === false ? 0 : 3,
      },
    },
    scales: {
      y: {
        suggestedMin: 0,
        stacked: stacked === true,
      },
    },
    interaction: {
      intersect: false,
      mode: "nearest",
      axis: "x",
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  const sum = data.reduce((a, b) => a + b.data.reduce((a, b) => a + (b.value || 0), 0), 0);

  return (
    <div className={styles.LineChart}>
      {!loading && (data.length === 0 || sum === 0) ? (
        <NoDataPoints />
      ) : (
        <>
          <div className={styles.legend}>
            {loading
              ? [...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className={styles["legend-row"]}
                  >
                    <Skeleton style={{ width: 80, height: 14 }} />
                  </div>
                ))
              : data.map((row, index) => {
                  return (
                    <div
                      key={"legend" + index}
                      className={styles["legend-row"]}
                    >
                      <div className={styles["legend-header"]}>
                        <span
                          className={styles["legend-color"]}
                          style={{
                            background: lightColorPalette[index % lightColorPalette.length],
                            borderColor: colorPalette[index % colorPalette.length],
                          }}
                        ></span>
                        <span className={styles["legend-label"]}>{row.label}</span>
                      </div>
                    </div>
                  );
                })}
          </div>
          <div className={styles.chart}>
            {loading ? (
              <Skeleton
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            ) : (
              <Line
                data={chartData}
                options={options}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
